import React, { useState, useEffect } from 'react';
import { Card } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Alert, AlertDescription } from "./components/ui/alert";
import { ContractorSection } from './ContractorSection';
import { ClientSection } from './ClientSection';
import { BillingSection } from './BillingSection';
import { translations } from './translations';
import LanguageSelector from './LanguageSelector';

// API Configuration
const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL || 'https://api.client.nimbicon.io',
  ENDPOINTS: {
    VALIDATE_TOKEN: '/validate-token',
    SUBMIT_FORM: '/submit-form',
    GET_SUBMISSION: '/get-submission'
  }
};

const MasterDataForm = () => {
  const [language, setLanguage] = useState('de');
  const [saveStatus, setSaveStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [tokenState, setTokenState] = useState({
    isValid: false,
    isChecking: true,
    formInstanceId: null,
    error: null
  });

  const t = translations[language];

  const [formData, setFormData] = useState({
  language: 'de',
    client: {
      company: '',
      street: '',
      zipCode: '',
      city: '',
      registration: '',
      vatId: '',
      representative: '',
      contractContactName: '',
      contractContactPhone: '',
      contractContactEmail: '',
      technicalContactName: '',
      technicalContactPhone: '',
      technicalContactEmail: '',
      billingAddress: ''
    },
    billing: {
      deliveryMethod: 'zugferd',
      billingEmail: '',
      xInvoiceRequired: false,
      routingId: '',
      portalRequired: false,
      portalUrl: '',
      postalRequired: false,
      poRequired: false,
      poNumber: '',
      projectRefRequired: false,
      projectId: '',
      costCenterRequired: false,
      costCenterNumber: '',
      otherRequirements: ''
    }
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Add this new function to validate required fields client-side
  const validateForm = () => {
    const errors = {};
    const requiredFields = [
      { path: 'client.company', label: t.client.company },
      { path: 'client.street', label: t.client.street },
      { path: 'client.zipCode', label: t.client.zipCode },
      { path: 'client.city', label: t.client.city },
      { path: 'client.contractContactName', label: `${t.client.contractContact} - Name` },
      { path: 'client.contractContactEmail', label: `${t.client.contractContact} - Email` }
    ];

    requiredFields.forEach(({ path, label }) => {
      const [section, field] = path.split('.');
      if (!formData[section]?.[field]) {
        errors[path] = t.validation.required.replace('{field}', label);
      }
    });

    // Validate email formats
    const emailFields = [
      { path: 'client.contractContactEmail', value: formData.client.contractContactEmail },
      { path: 'client.technicalContactEmail', value: formData.client.technicalContactEmail }
    ];
  
    emailFields.forEach(({ path, value }) => {
      if (value && !value.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
        errors[path] = t.validation.invalidEmail;
      }
    });
  
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const loadPreviousSubmission = async (formInstanceId) => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_SUBMISSION}?formInstanceId=${formInstanceId}&token=${token}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          }
        }
      );

      if (response.ok) {
        const { formData } = await response.json();
        if (formData) {
          setFormData(formData);
          // Set language from loaded data or fallback to browser preference (only upon initial load)
          if (!dataLoaded) {
            setLanguage(formData.language || navigator.language.split('-')[0] || 'de');
          }
            setDataLoaded(true); // Indicate that we loaded previous data
        }
      }
    } catch (error) {
      console.error("Error loading previous submission:", error);
      // We don't show an error to the user here as this is not critical
    }
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    setFormData(prev => ({
      ...prev,
      language: newLanguage
    }));
  };
  
  useEffect(() => {
    const validateToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      if (!token) {
        setTokenState({
          isValid: false,
          isChecking: false,
          error: t.errors.noToken
        });
        return;
      }

      try {
        const response = await fetch(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.VALIDATE_TOKEN}?token=${token}`,
          {
            method: 'GET',
            headers: {
              'Accept': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        setTokenState({
          isValid: data.isValid,
          isChecking: false,
          formInstanceId: data.formInstanceId,
          error: data.isValid ? null : t.errors.invalidToken
        });

        // If token is valid, try to load previous submission
        if (data.isValid && data.formInstanceId) {
          await loadPreviousSubmission(data.formInstanceId);
        }
      } catch (error) {
        console.error("Error validating token:", error);
        setTokenState({
          isValid: false,
          isChecking: false,
          error: t.errors.validationError
        });
      }
    };

    validateToken();
  }, []);

  useEffect(() => {
    if (tokenState.error) {
      // Aktualisiere nur die Fehlermeldung wenn sich die Sprache ändert
      setTokenState(prev => ({
        ...prev,
        error: prev.error ? t.errors[Object.keys(t.errors).find(key => 
          t.errors[key] === prev.error || 
          Object.values(translations['de'].errors).includes(prev.error) ||
          Object.values(translations['en'].errors).includes(prev.error)
        )] : null
      }));
    }
  }, [language, t.errors]);

  const handleSave = async () => {
    if (!tokenState.isValid) return;

    // Clear previous validation state
    setValidationErrors({});
    
    // Perform client-side validation
    if (!validateForm()) {
      setSaveStatus('validation-error');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setLoading(true);
    setSaveStatus(null);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUBMIT_FORM}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            token,
            formInstanceId: tokenState.formInstanceId,
            formData
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        // Handle server validation errors
        if (response.status === 400 && data.validationErrors) {
          setValidationErrors(data.validationErrors);
          setSaveStatus('validation-error');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        throw new Error(data.message || t.errors.unknownError);
      }

      setSaveStatus('success');
    } catch (error) {
      console.error('Error saving data:', error);
      setSaveStatus('error');
    } finally {
      setLoading(false);
    }
  };

  if (tokenState.isChecking) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert>
          <AlertDescription>{t.messages.validating}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (tokenState.error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertDescription>{tokenState.error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex justify-end">
        <LanguageSelector
          currentLanguage={language}
          onLanguageChange={handleLanguageChange}
          />
      </div>
      <h1 className="text-2xl font-bold text-center mb-8">{t.title}</h1>

      {saveStatus === 'validation-error' && Object.keys(validationErrors).length > 0 && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>
            <div className="font-bold mb-2">{t.validation.formErrors}</div>
            <ul className="list-disc pl-4">
              {Object.values(validationErrors).map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ContractorSection t={t} />
        <ClientSection 
          t={t}
          formData={formData}
          setFormData={setFormData}
          validationErrors={validationErrors}
        />
        </div>
        
        <BillingSection
          t={t}
          formData={formData}
          setFormData={setFormData}
          validationErrors={validationErrors}
        />

        <div className="flex justify-end">
          <Button 
            onClick={handleSave}
            disabled={loading || !tokenState.isValid}
          >
            {loading ? t.messages.saving : t.save}
          </Button>
        </div>

        {saveStatus && (
          <Alert variant={saveStatus === 'success' ? 'default' : 'destructive'}>
            <AlertDescription>
              {saveStatus === 'success' ? t.messages.saveSuccess : t.messages.saveError}
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};

export default MasterDataForm;